import React from "react";

const ChatLeftPanel = ({
  chats,
  activeChatId,
  onOpenChat,
  users = [],
  userSearch,
  setUserSearch,
  onCreateDirectChat,
  selectedCreateUserIds = [],
  onToggleCreateUser,
  onCreateSelectedChat,
  creatingChat,
  loggedUserId,
  chatMode = "direct",
  canUseGroupMode = false,
  onChangeChatMode,
  availableGroups = [],
  selectedGroupId,
  onSelectGroup,
  quotationOptions = [],
  selectedQuotationId,
  onSelectQuotation,
}) => {
  const normalizeOptionLabel = (value = "") =>
    String(value || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  const isGroupPlaceholderLabel = (label = "") => {
    const normalized = normalizeOptionLabel(label);
    return normalized === "select group" || normalized.startsWith("select group ");
  };
  const isProjectPlaceholderLabel = (label = "") => {
    const normalized = normalizeOptionLabel(label);
    return (
      normalized === "select project name" ||
      normalized === "select project" ||
      normalized.startsWith("select project name ") ||
      normalized.startsWith("select project ")
    );
  };

  const getDesignation = (user = {}) => {
    const level = String(user?.EmpAuthLevel || "").trim();
    if (level === "L0") return "Admin";
    if (level === "L1") return "Director";
    if (level === "L2") return "Finance";
    if (level === "L3") return "Sales Person";
    if (level === "L5") return "Accounting";
    if (level === "L4") {
      const roles = Array.isArray(user?.customRoles) ? user.customRoles.map((item) => String(item).toLowerCase()) : [];
      if (roles.includes("project manager")) return "Manager";
      if (roles.includes("operations")) return "Operation person";
    }
    return level || "Unknown";
  };

  const normalizedSearch = String(userSearch || "").trim().toLowerCase();
  const filteredUsers = users.filter((user) => {
    if (String(user?._id || "") === String(loggedUserId || "")) return false;
    const fullName = `${user?.EmpFirstName || ""} ${user?.EmpLastName || ""}`.toLowerCase();
    if (selectedCreateUserIds.includes(String(user?._id || ""))) return true;
    if (!normalizedSearch) return chatMode === "group";
    return fullName.includes(normalizedSearch);
  });

  const visibleChats = (chats || []).filter((chat) => {
    const chatId = String(chat?._id || chat?.id || chat?.chatId || "").trim();
    if (!chatId) return false;
    const members = Array.isArray(chat?.participants)
      ? chat.participants
      : Array.isArray(chat?.members)
        ? chat.members
        : [];
    // Show only actual created chats/entities, not plain user rows.
    return members.length >= 2 || Boolean(chat?.createdAt || chat?.updatedAt || chat?.createdBy || chat?.type);
  });
  const normalizedGroupOptions = (availableGroups || []).reduce((acc, group) => {
    const id = String(group?._id || group?.id || "").trim();
    const label = String(group?.newGroupName || group?.groupName || group?.name || "").trim();
    const loweredLabel = normalizeOptionLabel(label);
    if (!id || !label) return acc;
    if (isGroupPlaceholderLabel(label)) return acc;
    if (acc.some((item) => item.id === id || normalizeOptionLabel(item.label) === loweredLabel)) return acc;
    acc.push({ id, label });
    return acc;
  }, []);
  const normalizedProjectOptions = (quotationOptions || []).reduce((acc, project) => {
    const id = String(project?.quotationId || project?.projectId || "").trim();
    const label = String(project?.ProjectName || project?.projectName || "").trim();
    const loweredLabel = normalizeOptionLabel(label);
    if (!id || !label) return acc;
    if (isProjectPlaceholderLabel(label)) return acc;
    if (acc.some((item) => item.id === id || normalizeOptionLabel(item.label) === loweredLabel)) return acc;
    acc.push({ id, label });
    return acc;
  }, []);

  const resolveChatTitle = (chat = {}) => {
    const members = Array.isArray(chat?.participants)
      ? chat.participants
      : Array.isArray(chat?.members)
        ? chat.members
        : [];

    const type = String(chat?.type || "").toLowerCase();
    if (type === "group") {
      return chat?.name || chat?.displayName || chat?.groupName || "Unnamed Group";
    }

    const loggedId = String(loggedUserId || "");
    const otherMember = members.find((member) => {
      const id = String(member?._id || member?.id || member || "");
      return id && id !== loggedId;
    });

    const otherId = String(otherMember?._id || otherMember?.id || otherMember || "");
    const otherName = otherMember
      ? `${otherMember?.EmpFirstName || ""} ${otherMember?.EmpLastName || ""}`.trim() || otherMember?.name
      : "";
    const otherByEmployeeList = otherId
      ? users.find((emp) => String(emp?._id || "") === otherId)
      : null;
    const otherNameFromEmployees = otherByEmployeeList
      ? `${otherByEmployeeList?.EmpFirstName || ""} ${otherByEmployeeList?.EmpLastName || ""}`.trim() || otherByEmployeeList?.name
      : "";

    return otherName || otherNameFromEmployees || chat?.displayName || chat?.name || chat?.groupName || "Direct chat";
  };

  const resolveLastMessagePreview = (chat = {}) => {
    const nestedLastMessageObj = chat?.lastMessage || chat?.latestMessage || null;
    const nestedLast =
      chat?.lastMessage?.message ||
      chat?.lastMessage?.messageText ||
      chat?.lastMessage?.text ||
      chat?.lastMessage?.content ||
      chat?.latestMessage?.message ||
      chat?.latestMessage?.messageText ||
      chat?.latestMessage?.text ||
      chat?.latestMessage?.content;

    const topLevelLast =
      chat?.lastMessageText ||
      chat?.last_message ||
      chat?.lastText ||
      chat?.recentMessage ||
      chat?.message;

    const preview = String(nestedLast || topLevelLast || "").trim();
    if (preview) {
      const senderId = String(
        nestedLastMessageObj?.senderId?._id ||
          nestedLastMessageObj?.senderId ||
          nestedLastMessageObj?.sender?._id ||
          nestedLastMessageObj?.sender ||
          ""
      ).trim();
      const senderNameRaw =
        nestedLastMessageObj?.senderName ||
        nestedLastMessageObj?.senderId?.EmpFirstName ||
        nestedLastMessageObj?.sender?.EmpFirstName ||
        nestedLastMessageObj?.sender?.name ||
        "";
      const isOwn = senderId && String(loggedUserId || "") && senderId === String(loggedUserId || "");
      if (isOwn) return `You - ${preview}`;
      if (senderNameRaw) return `${senderNameRaw} - ${preview}`;
      return preview;
    }
    if (Number(chat?.unreadCount || 0) > 0) return "New unread message(s)";
    return "No messages yet";
  };

  return (
    <div className="w-[45%] border-r border-gray-200 bg-white rounded-l-md p-2 flex flex-col min-h-0">
      {canUseGroupMode ? (
        <div className="flex items-center gap-1 mb-2">
          <button
            onClick={() => onChangeChatMode?.("group")}
            className={`text-[11px] px-2 py-1 rounded ${chatMode === "group" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
          >
            Group chat
          </button>
          <button
            type="button"
            disabled
            className="text-[11px] px-2 py-1 rounded bg-gray-200 text-gray-400 cursor-not-allowed opacity-60"
            aria-disabled="true"
          >
            Direct chat
          </button>
        </div>
      ) : null}
      {chatMode === "group" ? (
        <div className="mb-2 space-y-1">
          <p className="text-[11px] font-medium text-gray-700">Group chat</p>
          <select
            value={selectedGroupId || ""}
            onChange={(event) => onSelectGroup?.(event.target.value)}
            className="w-full border rounded p-1 text-xs"
          >
            <option value="" disabled hidden>
              Select group
            </option>
            {normalizedGroupOptions.filter((group) => !isGroupPlaceholderLabel(group.label)).map((group) => {
              return (
                <option key={group.id} value={group.id}>
                  {group.label}
                </option>
              );
            })}
          </select>
          <select
            value={selectedQuotationId || ""}
            onChange={(event) => onSelectQuotation?.(event.target.value)}
            className="w-full border rounded p-1 text-xs"
            disabled={!selectedGroupId}
          >
            <option value="" disabled hidden>
              Select project name
            </option>
            {normalizedProjectOptions.filter((project) => !isProjectPlaceholderLabel(project.label)).map((project) => (
              <option key={project.id} value={project.id}>
                {project.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <input
        value={userSearch}
        onChange={(event) => setUserSearch(event.target.value)}
        placeholder="Search users"
        className="w-full border rounded p-1 text-xs mb-2"
      />
      <div className="max-h-[40%] overflow-auto mb-2">
        {!normalizedSearch ? (
          <p className="text-[11px] text-gray-500 px-1 py-1">
            {chatMode === "group"
              ? "Search/select users from selected project. Deselect any user before creating chat."
              : "Search by first or last name to find users."}
          </p>
        ) : null}
        {filteredUsers.map((user) => (
          <div
            key={user._id}
            className="w-full text-left text-xs p-2 mb-1 rounded border hover:bg-gray-50 flex items-center justify-between gap-2"
          >
            <button className="flex-1 text-left" onClick={() => onCreateDirectChat(user)}>
              <p>{user.EmpFirstName} {user.EmpLastName}</p>
              <p className="text-[10px] text-gray-500">{getDesignation(user)}</p>
            </button>
            <input
              type="checkbox"
              checked={selectedCreateUserIds.includes(String(user._id))}
              onChange={() => onToggleCreateUser(user)}
              title="Select for group chat"
            />
          </div>
        ))}
      </div>
      <button
        onClick={onCreateSelectedChat}
        disabled={creatingChat || selectedCreateUserIds.length < 1}
        className="w-full text-xs px-2 py-1 mb-2 rounded bg-blue-600 text-white disabled:opacity-60"
      >
        {creatingChat ? "Creating..." : `Create Chat (Selected Users: ${selectedCreateUserIds.length})`}
      </button>
      <div className="text-[11px] text-gray-500 mb-1">Chats</div>
      <div className="flex-1 min-h-0 overflow-auto">
        {visibleChats.map((chat) => {
          const chatId = chat?._id || chat?.id;
          const isActive = String(activeChatId || "") === String(chatId || "");
          const title = resolveChatTitle(chat);
          const lastMessage = resolveLastMessagePreview(chat);
          const unreadCount = Number(chat?.unreadCount || 0);
          return (
            <button
              key={chatId}
              onClick={() => onOpenChat(chat)}
              className={`w-full text-left text-xs p-2 mb-1 rounded border ${
                isActive ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className={`truncate ${isActive ? "text-blue-700 font-semibold" : ""}`}>{title}</p>
                {unreadCount > 0 ? (
                  <span className="min-w-5 px-1.5 py-0.5 text-[10px] text-white bg-red-600 rounded-full text-center">
                    {unreadCount}
                  </span>
                ) : null}
              </div>
              <p className="truncate text-[10px] text-gray-500">{lastMessage}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ChatLeftPanel;
